import React, { useState, useEffect } from "react";
import {
  collection, query, where, onSnapshot, addDoc,
  doc, updateDoc, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";

function fmtFecha(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleDateString("es-UY", {
    weekday: "long", day: "numeric", month: "long",
  });
}

function etiquetaDias(ts) {
  if (!ts?.toDate) return null;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fecha = ts.toDate();
  fecha.setHours(0, 0, 0, 0);
  const diff = Math.round((fecha - hoy) / (1000 * 60 * 60 * 24));
  if (diff === 0)  return { label: "Hoy",           color: "#dc2626", bg: "#fee2e2" };
  if (diff === 1)  return { label: "Mañana",         color: "#b45309", bg: "#fef3c7" };
  if (diff === -1) return { label: "Ayer",           color: "#64748b", bg: "#f1f5f9" };
  if (diff > 1)    return { label: `En ${diff} días`, color: "#1d4ed8", bg: "#eff6ff" };
  return { label: `Hace ${Math.abs(diff)} días`,     color: "#94a3b8", bg: "#f8fafc" };
}

export default function GestionCamiones() {
  const { profile } = useAuth();
  const navigate    = useNavigate();

  const [camiones,    setCamiones]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [tipo,        setTipo]        = useState("GIRA"); // "GIRA" | "CAMION"
  const [descripcion, setDescripcion] = useState("");
  const [fechaSalida, setFechaSalida] = useState("");
  const [guardando,   setGuardando]   = useState(false);
  const [verPasados,  setVerPasados]  = useState(false);
  const [sinAsignar,  setSinAsignar]  = useState([]);  // despachados Gira/Camión sin viaje
  const [asignarTarget, setAsignarTarget] = useState(null); // pedido a asignar a un viaje

  const deposito = profile?.deposito || null;

  // ── Listener de camiones del depósito ────────────────────
  useEffect(() => {
    if (!deposito) { setLoading(false); return; }
    const q = query(
      collection(db, "camiones"),
      where("deposito", "==", deposito),
    );
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ma = a.fechaSalida?.seconds ?? 0;
          const mb = b.fechaSalida?.seconds ?? 0;
          return ma - mb;
        });
      setCamiones(docs);
      setLoading(false);
    });
    return () => unsub();
  }, [deposito]);

  // ── Despachados Gira/Camión SIN viaje asignado (pendientes de asignar a mano) ──
  useEffect(() => {
    if (!deposito) return;
    const q = query(collection(db, "pedidos_despachados"), where("deposito", "==", deposito));
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(p =>
          (p.metodoEntrega === "GIRA" || p.metodoEntrega === "CAMION") &&
          !p.camionId &&
          p.cargaEstado !== "CARGADO"
        );
      setSinAsignar(docs);
    });
    return () => unsub();
  }, [deposito]);

  // ── Asignar manualmente un pedido a un viaje ──
  async function asignarAViaje(pedidoId, camionId) {
    try {
      await updateDoc(doc(db, "pedidos_despachados", pedidoId), { camionId });
      setAsignarTarget(null);
    } catch (e) {
      console.error("[GestionCamiones] no se pudo asignar:", e);
    }
  }

  // ── Crear camión ──────────────────────────────────────────
  async function crearCamion() {
    if (!descripcion.trim() || !fechaSalida) return;
    setGuardando(true);
    try {
      const [y, m, d] = fechaSalida.split("-").map(Number);
      const fechaDate = new Date(y, m - 1, d, 0, 0, 0);
      const destinoNorm = descripcion.trim()
        .normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();
      await addDoc(collection(db, "camiones"), {
        descripcion: descripcion.trim(),
        destino:     destinoNorm,            // normalizado, para el auto-match por descripción
        tipo,                                // "GIRA" | "CAMION"
        fechaSalida: Timestamp.fromDate(fechaDate),
        deposito,
        estado:     "ACTIVO",
        creadoAt:   serverTimestamp(),
        creadoPor:  profile?.uid || null,
      });
      setShowForm(false);
      setTipo("GIRA");
      setDescripcion("");
      setFechaSalida("");
    } finally {
      setGuardando(false);
    }
  }

  // ── Separar próximos / pasados ────────────────────────────
  const hoyMs = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
  const proximos = camiones.filter(c => {
    const ms = c.fechaSalida?.seconds ? c.fechaSalida.seconds * 1000 : 0;
    return ms >= hoyMs;
  });
  const pasados = camiones.filter(c => {
    const ms = c.fechaSalida?.seconds ? c.fechaSalida.seconds * 1000 : 0;
    return ms < hoyMs;
  }).reverse();

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="container" style={{ paddingBottom: "80px" }}>

      {/* Header */}
      <header className="topbar card" style={{ marginBottom: "16px" }}>
        <button onClick={() => navigate("/encargado/cola-despacho")} className="btn btn--ghost btn-sm">
          ⬅ Volver
        </button>
        <div>
          <h1 className="h1" style={{ margin: 0, fontSize: "17px" }}>🚚 Giras y Camiones</h1>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
            {deposito || "Sin depósito"}
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn btn--primary btn-sm">
          + Nuevo
        </button>
      </header>

      {/* ── Pendientes de asignar a un viaje ── */}
      {sinAsignar.length > 0 && (
        <div style={{ background: "#fff7ed", border: "1.5px solid #fdba74", borderRadius: "14px", padding: "12px 14px", marginBottom: "16px" }}>
          <p style={{ margin: "0 0 8px", fontSize: "13px", fontWeight: 800, color: "#9a3412" }}>
            ⚠️ {sinAsignar.length} pedido{sinAsignar.length !== 1 ? "s" : ""} despachado{sinAsignar.length !== 1 ? "s" : ""} sin viaje asignado
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {sinAsignar.map(p => (
              <div key={p.id} style={{ background: "#fff", border: "1px solid #fed7aa", borderRadius: "10px", padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "13px", color: "#0f172a" }}>
                    {p.numero || p.id} <span style={{ fontSize: "11px", fontWeight: 600, color: "#c2410c" }}>{p.metodoEntrega === "GIRA" ? "🚚 Gira" : "🚛 Camión"}{p.camionDestino ? ` · ${p.camionDestino}` : ""}</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.cliente}</div>
                </div>
                <button
                  onClick={() => setAsignarTarget(p)}
                  style={{ flexShrink: 0, padding: "8px 14px", borderRadius: "8px", border: "none", background: "#ea580c", color: "#fff", fontWeight: 700, fontSize: "12px", cursor: "pointer" }}
                >
                  Asignar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>Cargando…</div>
      ) : (
        <>
          {/* ── Próximos / actuales ── */}
          {proximos.length === 0 ? (
            <div style={{ textAlign: "center", padding: "64px 20px", color: "#94a3b8" }}>
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>🚚</div>
              <strong style={{ color: "#334155", display: "block", fontSize: "16px", marginBottom: "6px" }}>
                No hay camiones programados
              </strong>
              <span style={{ fontSize: "13px" }}>
                Tocá <strong>+ Nuevo</strong> para programar una salida
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {proximos.map(c => {
                const etiq = etiquetaDias(c.fechaSalida);
                const esHoy = etiq?.label === "Hoy";
                const cerrado = c.estado === "CERRADO";
                return (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/encargado/camiones/${c.id}`)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      background: cerrado ? "#f8fafc" : esHoy ? "#fefce8" : "#fff",
                      border: `1.5px solid ${cerrado ? "#e2e8f0" : esHoy ? "#fde68a" : "#e2e8f0"}`,
                      borderRadius: "12px", padding: "16px 18px",
                      cursor: "pointer", textAlign: "left", width: "100%",
                      opacity: cerrado ? 0.75 : 1,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "15px", color: "#0f172a" }}>
                        {c.tipo === "CAMION" ? "🚛" : "🚚"} {c.tipo === "CAMION" ? "Camión" : "Gira"}: {c.descripcion}
                      </div>
                      <div style={{ fontSize: "13px", color: "#64748b", marginTop: "3px" }}>
                        {fmtFecha(c.fechaSalida)}
                        {cerrado && typeof c.totalCargados === "number" ? ` · ${c.totalCargados} cargado${c.totalCargados !== 1 ? "s" : ""}` : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                      {cerrado ? (
                        <span style={{
                          fontSize: "12px", fontWeight: 700,
                          background: "#e2e8f0", color: "#475569",
                          padding: "4px 12px", borderRadius: "20px",
                        }}>
                          🔒 Cerrado
                        </span>
                      ) : etiq && (
                        <span style={{
                          fontSize: "12px", fontWeight: 700,
                          background: etiq.bg, color: etiq.color,
                          padding: "4px 12px", borderRadius: "20px",
                        }}>
                          {etiq.label}
                        </span>
                      )}
                      <span style={{ fontSize: "18px", color: "#94a3b8" }}>›</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Historial de pasados ── */}
          {pasados.length > 0 && (
            <div style={{ marginTop: "24px" }}>
              <button
                onClick={() => setVerPasados(v => !v)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: "13px", color: "#64748b", padding: "4px 0",
                  display: "flex", alignItems: "center", gap: "6px",
                }}
              >
                <span>{verPasados ? "▾" : "▸"}</span>
                Historial de salidas ({pasados.length})
              </button>

              {verPasados && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }}>
                  {pasados.map(c => (
                    <button
                      key={c.id}
                      onClick={() => navigate(`/encargado/camiones/${c.id}`)}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        background: "#f8fafc", border: "1.5px solid #e2e8f0",
                        borderRadius: "10px", padding: "12px 14px",
                        cursor: "pointer", textAlign: "left", width: "100%", opacity: 0.85,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "14px", color: "#334155" }}>
                          {c.tipo === "CAMION" ? "🚛" : "🚚"} {c.tipo === "CAMION" ? "Camión" : "Gira"}: {c.descripcion}
                        </div>
                        <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>
                          {fmtFecha(c.fechaSalida)}
                        </div>
                      </div>
                      <span style={{ fontSize: "12px", color: "#94a3b8" }}>Ver →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Modal: nuevo camión ── */}
      {showForm && (
        <div
          onClick={() => setShowForm(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 1000, display: "flex", alignItems: "flex-end",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", width: "100%",
              borderRadius: "16px 16px 0 0", padding: "24px 20px 32px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700 }}>🚚 Nuevo viaje</h3>
              <button
                onClick={() => setShowForm(false)}
                style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}
              >×</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Tipo: Gira o Camión */}
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "5px" }}>
                  Tipo de viaje
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  {[
                    { id: "GIRA",   label: "🚚 Gira" },
                    { id: "CAMION", label: "🚛 Camión" },
                  ].map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTipo(t.id)}
                      style={{
                        flex: 1, padding: "10px", borderRadius: "10px",
                        border: `1.5px solid ${tipo === t.id ? "#0f172a" : "#e2e8f0"}`,
                        background: tipo === t.id ? "#0f172a" : "#fff",
                        color: tipo === t.id ? "#fff" : "#475569",
                        fontWeight: 700, fontSize: "14px", cursor: "pointer",
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "5px" }}>
                  Destino (lo que ponen los vendedores en la descripción)
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="Ej: Rivera, Artigas, Norte..."
                  value={descripcion}
                  onChange={e => setDescripcion(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", display: "block", marginBottom: "5px" }}>
                  Fecha de salida
                </label>
                <input
                  type="date"
                  className="input"
                  value={fechaSalida}
                  onChange={e => setFechaSalida(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              <button
                onClick={crearCamion}
                disabled={guardando || !descripcion.trim() || !fechaSalida}
                className="btn btn--primary"
                style={{ width: "100%", padding: "14px", fontSize: "15px", fontWeight: 700, marginTop: "4px" }}
              >
                {guardando ? "Creando…" : `Crear ${tipo === "GIRA" ? "Gira" : "Camión"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: asignar pedido a un viaje ── */}
      {asignarTarget && (() => {
        const tipoPedido = asignarTarget.metodoEntrega; // "GIRA" | "CAMION"
        const viajesCompat = proximos.filter(c => (c.tipo || "CAMION") === tipoPedido && c.estado !== "CERRADO");
        return (
          <div onClick={() => setAsignarTarget(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}>
            <div onClick={e => e.stopPropagation()} style={{ background: "#fff", width: "100%", borderRadius: "16px 16px 0 0", padding: "20px", maxHeight: "70vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
                  Asignar #{asignarTarget.numero || asignarTarget.id}
                </h3>
                <button onClick={() => setAsignarTarget(null)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>×</button>
              </div>
              <p style={{ margin: "0 0 14px", fontSize: "13px", color: "#64748b" }}>
                Elegí a qué {tipoPedido === "GIRA" ? "gira" : "camión"} va este pedido.
              </p>
              {viajesCompat.length === 0 ? (
                <p style={{ fontSize: "13px", color: "#94a3b8", textAlign: "center", padding: "20px 0" }}>
                  No hay {tipoPedido === "GIRA" ? "giras" : "camiones"} programados. Creá uno con <strong>+ Nuevo</strong>.
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {viajesCompat.map(c => (
                    <button
                      key={c.id}
                      onClick={() => asignarAViaje(asignarTarget.id, c.id)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: "10px", padding: "12px 14px", cursor: "pointer", textAlign: "left", width: "100%" }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
                          {c.tipo === "CAMION" ? "🚛" : "🚚"} {c.descripcion}
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>{fmtFecha(c.fechaSalida)}</div>
                      </div>
                      <span style={{ fontSize: "13px", color: "#2563eb", fontWeight: 700 }}>Asignar →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
